import { computed, isRef } from 'vue'

/**
 * Quantity precision helpers (composable)
 *
 * What it does
 * - Normalizes the step (interval) and the minimum value
 * - Derives decimal precision from the step (supports scientific notation, e.g., 1e-7)
 * - Rounds calculations to the step precision
 * - Validates step multiples and minimum value using a tolerance (epsilon)
 *
 * Why
 * - Floating‑point arithmetic (e.g., 0.6 × 3) can yield 1.799999999… and break naive modulo checks
 * - We need consistent, step‑based rounding and validation across components
 * - Epsilon tolerance prevents rejecting mathematically correct values that differ by ~1 ULP
 *
 * Usage
 * const { STEP_EPSILON, normalizedStep, roundToStepPrecision, isMultipleOfStep, validateQuantity } =
 *   useQuantityUtils(step, min)
 *
 * API
 * - STEP_EPSILON: number — tolerance used for comparisons
 * - normalizedStep: Computed<number> — positive step (defaults to 1)
 * - stepDecimals: Computed<number> — number of decimal places derived from the step
 * - roundToStepPrecision(value: number): number — rounding to the step precision
 * - isMultipleOfStep(quantity: number): boolean — safe multiple‑of‑step check
 * - validateQuantity(quantity: number): boolean — validates min + multiple of step
 */

type MaybeRef<T> = T | { value: T }

export default function useQuantityUtils(
  stepInput: MaybeRef<number | string | undefined | null>,
  minInput?: MaybeRef<number | undefined | null>
) {
  const STEP_EPSILON = 1e-9

  const productStep = computed<number | string | undefined | null>(() => unrefAny(stepInput))
  const productMin = computed<number | undefined | null>(() => unrefAny(minInput))

  const normalizedStep = computed<number>(() => {
    const raw = Number(productStep.value)
    return Number.isFinite(raw) && raw > 0 ? raw : 1
  })

  const stepDecimals = computed<number>(() => {
    const step = normalizedStep.value
    const str = step.toString()
    const sci = str.split('e-')
    if (sci.length === 2) {
      return parseInt(sci[1] || '0', 10)
    }
    const dot = str.split('.')
    return dot[1] ? dot[1].length : 0
  })

  const roundToStepPrecision = (value: number): number => {
    return Number(value.toFixed(stepDecimals.value))
  }

  const isMultipleOfStep = (quantity: number): boolean => {
    const step = normalizedStep.value
    if (!Number.isFinite(quantity)) return false
    if (step === 0) return true
    const ratio = quantity / step
    const nearest = Math.round(ratio)
    return Math.abs(ratio - nearest) < STEP_EPSILON
  }

  const validateQuantity = (quantity: number): boolean => {
    const min = Number(productMin.value ?? 1)
    if (quantity + STEP_EPSILON < min) return false
    return isMultipleOfStep(quantity)
  }

  return {
    STEP_EPSILON,
    normalizedStep,
    stepDecimals,
    roundToStepPrecision,
    isMultipleOfStep,
    validateQuantity,
  }
}

function unrefAny<T>(val: MaybeRef<T>): T {
  return isRef(val as any) ? (val as any).value : (val as T)
}
